import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { ArrowLeft } from 'lucide-react'

export default function Legal() {
  const navigate = useNavigate()

  const BackButton = () => (
    <Button 
      variant="outline" 
      onClick={() => navigate(-1)}
      className="mb-6"
      data-testid="back-to-app-btn"
    >
      <ArrowLeft className="w-4 h-4 mr-2" />
      Back to App
    </Button>
  )

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 lg:py-12">
        <BackButton />
        
        <h1 className="text-4xl lg:text-5xl font-bold tracking-tight font-['Barlow_Condensed'] mb-4">
          LEGAL INFORMATION
        </h1>
        
        {/* Last Updated Notice */}
        <p className="text-xs text-muted-foreground mb-8">
          These documents were last updated 03/08/2026. Continued use constitutes acceptance.
        </p>

        <div className="space-y-8">
          {/* Terms of Service */}
          <Card>
            <CardContent className="p-6 lg:p-8">
              <h2 className="text-2xl lg:text-3xl font-bold font-['Barlow_Condensed'] mb-4 text-primary">
                TERMS OF SERVICE
              </h2>
              
              <div className="space-y-4 text-base lg:text-lg leading-relaxed">
                <p className="text-muted-foreground">
                  By using DriverPNL ("the Service"), you agree to these Terms. If you do not agree, do not use the Service.
                </p>

                <div>
                  <h3 className="font-semibold mb-2">User Responsibilities</h3>
                  <p className="text-muted-foreground">
                    You must provide accurate information, keep your account credentials secure, and use the Service only for lawful purposes in compliance with all applicable laws. You are solely responsible for the accuracy of all data you enter (expenses, revenue, mileage, per diem, etc.) and any resulting financial or tax consequences.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Service "As Is" – No Warranty</h3>
                  <p className="text-muted-foreground">
                    The Service is provided "as is" and "as available" without any warranties, express or implied, including merchantability, fitness for a particular purpose, or non-infringement.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Limitation of Liability</h3>
                  <p className="text-muted-foreground">
                    To the maximum extent permitted by law, DriverPNL and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages—including loss of profits, data, or business opportunities—arising from your use of the Service, even if advised of such possibility. Our total liability shall not exceed the amount you paid us in the 12 months prior to any claim.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Indemnification</h3>
                  <p className="text-muted-foreground">
                    You agree to indemnify and hold harmless DriverPNL from any claims, losses, or expenses (including reasonable attorneys' fees) arising from your use of the Service or violation of these Terms.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Governing Law & Disputes</h3>
                  <p className="text-muted-foreground">
                    These Terms are governed by the laws of the State of Indiana, USA. Any disputes shall be resolved exclusively through binding arbitration in Indianapolis, Indiana, under the rules of the American Arbitration Association.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Refund Policy</h3>
                  <p className="text-muted-foreground">
                    Subscriptions are non-refundable after any free trial period ends. You may cancel anytime, but no partial refunds are provided for the remaining billing period.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Changes to Terms</h3>
                  <p className="text-muted-foreground">
                    We may update these Terms at any time. Continued use after changes constitutes acceptance.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Termination</h3>
                  <p className="text-muted-foreground">
                    We may suspend or terminate your access at any time for violations of these Terms.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Contact</h3>
                  <p className="text-muted-foreground">
                    Questions? Reach us at support@driverpnl.com.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Policy */}
          <Card>
            <CardContent className="p-6 lg:p-8">
              <h2 className="text-2xl lg:text-3xl font-bold font-['Barlow_Condensed'] mb-4 text-primary">
                PRIVACY POLICY
              </h2>
              
              <div className="space-y-4 text-base lg:text-lg leading-relaxed">
                <div>
                  <h3 className="font-semibold mb-2">Data We Collect</h3>
                  <p className="text-muted-foreground mb-2">
                    To provide the Service, we collect:
                  </p>
                  <ul className="text-muted-foreground italic list-disc list-inside space-y-1">
                    <li>Account information (email, credentials)</li>
                    <li>Uploaded receipt images</li>
                    <li>Expense, revenue, per diem, and mileage entries</li>
                    <li>Basic usage data (to improve the app)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">How We Use & Store Data</h3>
                  <p className="text-muted-foreground">
                    Your data is used only to deliver the Service (tracking, reports, account management) and improve functionality. Data is stored securely in an encrypted cloud infrastructure with encryption at rest and in transit. We do not sell, rent, or share your personal data with third parties for marketing purposes.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Your Rights</h3>
                  <p className="text-muted-foreground">
                    You may request access, correction, or deletion of your data at any time. Contact us to export your data or delete your account. Upon deletion, data is permanently removed within 30 days.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Security</h3>
                  <p className="text-muted-foreground">
                    We use industry-standard measures (HTTPS/TLS, secure hashing, regular audits). No system is 100% secure, and we cannot guarantee absolute protection.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Children's Privacy</h3>
                  <p className="text-muted-foreground">
                    The Service is not intended for users under 13. We do not knowingly collect data from children under 13.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Contact</h3>
                  <p className="text-muted-foreground">
                    Privacy questions? Email support@driverpnl.com.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Important Disclaimers */}
          <Card className="border-amber-500/50 bg-amber-500/5">
            <CardContent className="p-6 lg:p-8">
              <h2 className="text-2xl lg:text-3xl font-bold font-['Barlow_Condensed'] mb-4 text-amber-500">
                IMPORTANT DISCLAIMERS
              </h2>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
                Accounting & Legal Disclaimers
              </p>
              
              <div className="space-y-4 text-base lg:text-lg leading-relaxed">
                <p className="text-muted-foreground">
                  DriverPNL provides tools to track expenses, revenue, per diem, and generate profit & loss reports. <strong className="text-foreground italic">It is not tax, accounting, legal, or financial advice.</strong>
                </p>
                
                <p className="text-muted-foreground">
                  Per diem calculations are estimates based on current publicly available IRS rates and may not apply to your specific situation. Expense categorization and deductibility are user-controlled and approximate only.
                </p>
                
                <p className="text-muted-foreground">
                  The app is not certified for ELD, HOS, IFTA, FMCSA, or any regulatory compliance. Always verify per diem, deductions, and records with a qualified tax professional or the IRS.
                </p>
                
                <p className="text-muted-foreground font-medium text-foreground">
                  Use the Service at your own risk. DriverPNL is not responsible for errors, omissions, or any tax, financial, or legal consequences resulting from its use.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center pb-8">
            <BackButton />
          </div>
        </div>
      </div>
    </div>
  )
}
